/* ---------------------------------------------------------------------------
   The live 3D viewport on a bridge's record.

   The modeller's real Canvas3D, framed at the size of the hero panel with its
   workspace chrome hidden — so the model on a structure's record can be orbited
   rather than looked at. `?embed=viewport` is what strips the chrome; see
   ApplyEmbedMode in the vendored App.ts.

   THREE PROBLEMS SHAPE THIS FILE, and the first two are the same two that shaped
   ./overlay.ts.

   An iframe cannot live inside the render root. Moving an iframe in the DOM
   reloads it — restarting a 1.6 MB download and throwing away the user's camera
   position — and while src/core/dom.ts reconciles rather than replacing, it makes
   no promise about a given node surviving: a structural change anywhere at or
   above this one would take the frame with it. Too sharp an edge to leave under a
   3D viewport, so the frame is parked in a container appended to document.body
   and positioned over the hole the record leaves for it. That is the standard
   trick for this, and it is why `sync` measures a placeholder rather than
   appending anything to it.

   A viewport costs a WebGL context. Chrome caps a tab at around sixteen and the
   modeller holds one for as long as it is alive, so exactly one of these exists
   at a time: changing bridge, leaving the record, or opening the full modeller
   all tear it down. Reopening reloads from Cloudflare, which is the same path a
   fresh session takes and therefore the one that gets exercised.

   And a frame on document.body is clipped by nothing. The placeholder sits in a
   panel that scrolls inside a pane that hides its overflow, and none of that
   applies to a fixed element parked elsewhere — so the frame has to be cropped to
   the visible part of the placeholder by hand. See cropTo below; without it, the
   3D view rides up over the page header the moment the record is scrolled.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import type { ComponentNode } from '../data/types.ts';
import { heroModel, meshAvailability } from '../data/derive.ts';

/** Where the frame lives — a sibling of the render root, not inside it. */
let host: HTMLDivElement | null = null;
let frame: HTMLIFrameElement | null = null;

/**
 * What the live frame is showing. A change to any part of it rebuilds the frame.
 *
 * `theme` is in here because the modeller reads it once, off its own URL, at boot.
 * Rebuilding on a theme toggle costs the user's camera position, which is a poor
 * trade in general — but a theme toggle is rare and deliberate, and the alternative
 * is a message protocol in the vendored tree for something nobody asked for.
 */
let showing: { bridgeId: string; modelId: string; theme: string } | null = null;

/** Cancels the follow loop that keeps `host` over the placeholder. */
let stopTracking: (() => void) | null = null;

/** The ancestors that crop the placeholder, resolved once per placeholder — see clippers. */
let clipping: { slot: HTMLElement; ancestors: HTMLElement[] } | null = null;

/**
 * Class the record's placeholder carries.
 *
 * Exported because src/screens/detail.ts renders the placeholder and this module
 * finds it — one name, defined once, rather than a string literal in two files.
 */
export const PLACEHOLDER = 'det-hero__live';

/* --------------------------- geometry --------------------------- */

/** Sides of a rectangle in viewport coordinates. */
export interface Sides {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * How much of `box` has to be cropped for nothing to show outside `limit`.
 *
 * The frame is `position: fixed` on document.body, so none of the boxes the
 * placeholder sits inside clip it — the record's left panel scrolls, and a frame
 * following the placeholder up out of it used to carry on over the page header.
 * Cropping is done with clip-path rather than by shrinking the host: the modeller
 * frames its camera from the size of its container, so resizing it while someone
 * scrolls would re-frame the bridge on every frame.
 *
 * Exported for the smoke tests — jsdom has no layout, so the arithmetic is checked
 * here rather than through a rendered record.
 */
export function cropTo(box: Sides, limit: Sides): Sides & { hidden: boolean } {
  const top = Math.max(0, limit.top - box.top);
  const right = Math.max(0, box.right - limit.right);
  const bottom = Math.max(0, box.bottom - limit.bottom);
  const left = Math.max(0, limit.left - box.left);
  return {
    top,
    right,
    bottom,
    left,
    hidden: top + bottom >= box.bottom - box.top || left + right >= box.right - box.left,
  };
}

/**
 * The ancestors of the placeholder that crop it.
 *
 * Resolved once and cached against the placeholder itself: render() patches that
 * element in place rather than replacing it (see src/core/dom.ts), so the chain
 * above it is stable for as long as the record is up — whereas getComputedStyle on
 * every ancestor on every animation frame is not something to do for free.
 */
function clippers(slot: HTMLElement): HTMLElement[] {
  if (clipping?.slot === slot) return clipping.ancestors;

  const found: HTMLElement[] = [];
  for (let el = slot.parentElement; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') found.push(el);
  }
  clipping = { slot, ancestors: found };
  return found;
}

/**
 * Move the frame over the placeholder, cropped to as much of it as is on show.
 *
 * Position is copied from the placeholder's box on every animation frame while the
 * viewport is up. A ResizeObserver would catch the placeholder changing size but
 * not the page scrolling under it, and an IntersectionObserver neither; the record
 * is a scrolling pane, so the cheapest correct answer is to follow it. A handful of
 * `getBoundingClientRect` calls per frame is nothing next to the WebGL render loop
 * it sits on top of.
 *
 * Hidden rather than removed when the placeholder is off screen or gone, so a
 * scroll does not cost a reload.
 */
function track(): void {
  let raf = 0;

  const step = (): void => {
    raf = requestAnimationFrame(step);
    if (!host) return;

    const slot = document.querySelector<HTMLElement>(`.${PLACEHOLDER}`);
    if (!slot) {
      host.style.visibility = 'hidden';
      return;
    }

    const box = slot.getBoundingClientRect();
    // Zero-area means the pane is collapsed or the element is display:none in a
    // print view; either way there is nothing to sit over.
    if (box.width < 2 || box.height < 2) {
      host.style.visibility = 'hidden';
      return;
    }

    /* Every box the placeholder is cropped by, the window included — the record's
       panel scrolls, and the page itself can too. */
    let limit: Sides = {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    };
    for (const el of clippers(slot)) {
      const r = el.getBoundingClientRect();
      limit = {
        top: Math.max(limit.top, r.top),
        left: Math.max(limit.left, r.left),
        right: Math.min(limit.right, r.right),
        bottom: Math.min(limit.bottom, r.bottom),
      };
    }

    const crop = cropTo(box, limit);
    if (crop.hidden) {
      host.style.visibility = 'hidden';
      return;
    }

    host.style.visibility = 'visible';
    host.style.transform = `translate(${box.left}px, ${box.top}px)`;
    host.style.width = `${box.width}px`;
    host.style.height = `${box.height}px`;
    /* Only while something is actually cropped: an inset() with no round of its own
       would square off the corners `.det-live`'s border-radius gives it, so the
       ordinary case is left exactly as it was. */
    host.style.clipPath =
      crop.top || crop.right || crop.bottom || crop.left
        ? `inset(${crop.top}px ${crop.right}px ${crop.bottom}px ${crop.left}px)`
        : '';
  };

  raf = requestAnimationFrame(step);
  stopTracking = () => cancelAnimationFrame(raf);
}

/* --------------------------- lifecycle --------------------------- */

/**
 * The URL the embedded viewport loads.
 *
 * Exported for scripts/smoke.ts, which checks every parameter name the vendored
 * App.ts reads against the ones the register produces. `embed` is only ever written
 * here, so a test that sampled only overlay.ts's frameSrc would miss it.
 */
export function viewportSrc(s: Readonly<State>, bridgeId: string, modelId: string): string {
  return `modeler?${new URLSearchParams({
    bridge: bridgeId,
    model: modelId,
    mode: 'modeler',
    embed: 'viewport',
    theme: s.theme,
  })}`;
}

function build(s: Readonly<State>, bridgeId: string, modelId: string): void {
  frame = h('iframe', {
    class: 'det-live__frame',
    src: viewportSrc(s, bridgeId, modelId),
    title: 'Rotate the 3D model',
    // Same reasoning as the full-screen frame in ./overlay.ts: allow-same-origin is
    // required for the storage shim, so this is not a security boundary against the
    // vendored code — what it buys is that nothing in there can navigate the
    // register away. No downloads or modals here: this is a viewport, and the export
    // and import paths live in the full modeller.
    sandbox: 'allow-scripts allow-same-origin',
  });

  host = h('div', { class: 'det-live' }, frame);
  document.body.appendChild(host);
  showing = { bridgeId, modelId, theme: s.theme };
  // Not ready until THIS frame posts its own `ready` — see wireViewportMessages.
  // Reset here rather than trusted to carry over: the previous bridge's frame
  // may already have reported ready, and that must not paint over the
  // placeholder as if the new one had too.
  actions.setHeroViewportReady(false);
  // Same reasoning: the previous frame's tree must not be shown against this
  // one until THIS frame reports its own — see wireViewportMessages below.
  actions.setHeroComponentTree(null);
  // A label path from one bridge's tree can coincidentally match another's
  // (every bridge has a "Spans>Span-1"), so a leftover expanded set would
  // silently un-collapse nodes in a tree the user has never opened before.
  actions.resetComponentTreeExpanded();
  track();
}

function destroy(): void {
  stopTracking?.();
  stopTracking = null;
  host?.remove();
  host = null;
  frame = null;
  showing = null;
  // Holds a placeholder that is about to leave the document.
  clipping = null;
  actions.setHeroViewportReady(false);
  actions.setHeroComponentTree(null);
  actions.resetComponentTreeExpanded();
}

/**
 * Reconcile the viewport with state. Called from paint() on every render.
 *
 * Mounted only on the bridge record, only when there is a model with something in
 * it to draw, and only while the full-screen modeller is closed — two live WebGL
 * contexts for the same model is waste, and the overlay covers this anyway.
 *
 * "Something in it to draw" is `heroModel`, and it deliberately is not "a model
 * exists": mounting this on the empty shell the register mints before an import
 * would boot the modeller on a seed template and orbit a default bridge that has
 * nothing to do with the structure on the record. See derive.ts for the three kinds
 * of evidence that count, and why a captured still stopped being one of them on its
 * own.
 */
export function syncModelerViewport(s: Readonly<State>): void {
  const model = heroModel(s.models.models);

  /* THE STORED MESH WINS, AND THAT IS THE WHOLE POINT OF THIS GUARD. A model with
     geometry in the bucket is rendered from that file by src/modeler/meshView.ts —
     the source's own triangles, identical on every device, with no parametric engine
     anywhere near it. Framing the modeller for the same model would put a second
     WebGL context over the first, draw a reconstruction of a bridge that has already
     been drawn, and give this model's state another writer. So this whole module is
     now the fallback for a model that has no geometry to render: one imported before
     the derivation existed, or from an export that carried no mesh. */
  if (model?.hasGeometry) {
    if (host) destroy();
    return;
  }
  /* Unmounted while an import is running, and that one is not about waste. The
     import drives a modeller of its own on this same model
     (src/modeler/importModel.ts), and two framed modellers on one model are two
     writers of `bim_modeler_model_state_<id>` — this one's autosave would put
     the pre-import state back over the imported one, last-write-wins, with
     nothing to show that it had. The record shows the import's progress in this
     slot meanwhile, and the viewport comes back on the imported model. */
  const importing = s.modelImport?.bridgeId === s.selectedId;
  /* AND NOT UNTIL THIS DEVICE IS KNOWN TO HOLD THE GEOMETRY. Not a nicety: a
     modeller that cannot find an imported mesh rebuilds the bridge from its
     parameters and says so in an alert this sandbox swallows, so mounting on a
     `'checking'` or `'absent'` model draws a plausible, wrong bridge over a correct
     still. See meshAvailability, which is where the whole account is. */
  const ready = model !== null && meshAvailability(model, s.modelMeshState) === 'ready';
  const wanted =
    s.screen === 'detail' && !importing && ready && model
      ? { bridgeId: s.selectedId, modelId: model.id, theme: s.theme }
      : null;

  if (!wanted) {
    if (host) destroy();
    return;
  }

  const stale =
    showing !== null &&
    (showing.bridgeId !== wanted.bridgeId ||
      showing.modelId !== wanted.modelId ||
      showing.theme !== wanted.theme);
  if (stale) destroy();

  if (!host) build(s, wanted.bridgeId, wanted.modelId);
}

/**
 * Listen for this viewport's own `ready`.
 *
 * Called once at boot, alongside overlay.ts's wireModelerMessages — the two are
 * independent listeners rather than one shared dispatcher, because each owns a
 * different frame and the two frames never coexist (this one is always torn
 * down before the full-screen overlay opens; see syncModelerViewport above).
 * `frame` being non-null is therefore enough to know a `ready` is this one's,
 * with no need to match it against a bridge or model id.
 */
export function wireViewportMessages(): void {
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    // This one's frame only — an import runs a second, hidden modeller that
    // posts the same messages, and it is unmounted rather than absent, so
    // `frame` alone is no longer the whole test.
    if (!frame || event.source !== frame.contentWindow) return;
    const data = event.data as { source?: string; type?: string; roots?: unknown } | null;
    if (data?.source !== 'bridge-modeler') return;

    if (data.type === 'ready') {
      actions.setHeroViewportReady(true);
      // The component tree exists only in the frame's own memory — nothing
      // to read out of storage — so it has to be asked for, and only once
      // there is actually a model loaded to ask about.
      frame.contentWindow?.postMessage(
        { source: 'bridge-monitor', type: 'request-component-tree' },
        window.location.origin,
      );
      return;
    }

    if (data.type === 'component-tree' && Array.isArray(data.roots)) {
      actions.setHeroComponentTree(data.roots as ComponentNode[]);
    }
  });
}
